import { useEffect, useState } from 'react';
import {
  getFeedsCommentsEditLog,
  getNewsCommentsEditLog,
  updateFeedsCommentsInfo,
  updateNewsCommentsInfo,
} from '../../services/feeds';
import moment from 'moment';
import { useIntl } from 'umi';
import './index.scss';
import { notificationSuccess } from '@op/pages/report/gpt/constant';
import { DBO_ARR } from '@op/utils/format';
export type PageUpdateType = 'news' | 'common';

export default function useFeedsUpdate<T>({
  defaultValue,
  oldValue,
  filter,
  autoLoad = true,
  showSystemLog = true,
  type = 'common',
  subTopic,
}: {
  defaultValue: T;
  oldValue: T;
  filter: any;
  autoLoad?: boolean;
  showSystemLog?: boolean;
  type?: PageUpdateType;
  subTopic?: Array<any>;
}) {
  const { formatMessage } = useIntl();

  function submitSentimentTranslateInfo(params: any, callBack?: () => void) {
    const updateDataFunc =
      type === 'common' ? updateFeedsCommentsInfo : updateNewsCommentsInfo;
    return updateDataFunc({
      ...params,
      comment_time: moment(params.comment_time).format('YYYY-MM-DD HH:mm:ss'),
    }).then(() => {
      callBack?.();
      notificationSuccess(formatMessage({ id: 'Edit Success' }) + '！');

      return true;
    });
  }

  function getSentimentTranslateLog(params: any, defaultValue: any) {
    const getDataFunc =
      type === 'common' ? getFeedsCommentsEditLog : getNewsCommentsEditLog;
    return getDataFunc(params).then((data: any) => {
      let list = data?.list || [];

      if (showSystemLog) {
        let len = list.length;
        if (Array.isArray(defaultValue)) {
          if (len > 0) {
            list.sort(DBO_ARR.sortByTime('create_time'));
            defaultValue.map((val) => {
              const currentTopicList = list.filter(
                (each) => each.topic === val.topic,
              )[0];
              return {
                ...val,
                new_value: currentTopicList?.old_value || val.new_value,
              };
            });
          }
          list.push(...defaultValue);
        } else {
          if (len > 0) {
            defaultValue['new_value'] = list[len - 1]?.old_value;
          }
          list.push(defaultValue);
        }
      }
      return list;
    });
  }

  const [newValue, setNewValue] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(false);
  const [modifiedValue, setModifiedValue] = useState<T>(oldValue);

  const submit = (close?: () => void, value?: string) => {
    setLoading(true);
    submitSentimentTranslateInfo(
      {
        ...(filter || {}),
        old_value: String(
          logList.length === 1 && showSystemLog ? oldValue : modifiedValue,
        ),
        new_value: String(value || newValue),
      },
      close,
    ).finally(() => {
      setLoading(false);
    });
  };

  const submitSubTopic = async (
    value: { topic: string; sentiment: number; old_sentiment: number }[],
  ) => {
    setLoading(true);
    for (const item of value) {
      await submitSentimentTranslateInfo({
        ...(filter || {}),
        old_value: String(item.old_sentiment),
        new_value: String(item.sentiment),
        type: 6,
        topic: item.topic,
      });
    }
    setLoading(false);
  };

  const [logList, setLogList] = useState<Array<any>>([]);
  const [logApiError, setLogApiError] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [subTopicValue, setSubTopicValue] = useState<Array<any>>(
    subTopic || [],
  );

  const getLogList = () => {
    setLogLoading(true);
    if (!filter) return;

    const requestArr = [
      getSentimentTranslateLog(
        { ...filter, page: 1, page_size: 1000 },
        {
          ...filter,
          editor: 'System',
          create_time:
            type === 'news' ? filter.release_time : filter?.comment_time,
          old_value: defaultValue,
          new_value: defaultValue,
        },
      ),
    ];

    if (Array.isArray(subTopicValue) && subTopicValue.length > 0) {
      requestArr.push(
        getSentimentTranslateLog(
          { ...filter, page: 1, page_size: 1000, type: 6 },
          subTopicValue.map((val) => ({
            ...filter,
            editor: 'System',
            create_time: filter?.comment_time,
            old_value: val.sentiment,
            new_value: val.sentiment,
            topic: val.topic,
          })),
        ),
      );
    }

    function sortByTime(a: any, b: any) {
      return (
        new Date(b.create_time).getTime() - new Date(a.create_time).getTime()
      );
    }

    function setMainData(data: Array<any>) {
      let log = data?.[0]?.new_value;
      if (log) {
        setNewValue(log);
        setModifiedValue(log);
      }
    }

    Promise.all(requestArr)
      .then(([data, secondData]) => {
        data = typeof data !== 'boolean' ? data : [];
        if (secondData) {
          secondData = Array.isArray(secondData) ? secondData : [];
          const logList = [...data, ...secondData].sort(sortByTime);
          const sortSecondData = secondData.sort(sortByTime);
          const latestSubTopic = subTopicValue?.reduce((result, newItem) => {
            const selected = sortSecondData?.filter(
              (item: { topic: string }) => item.topic === newItem.topic,
            )[0];
            result.push({
              ...newItem,
              sentiment: Number(selected.new_value),
            });

            return result;
          }, []);

          setSubTopicValue(latestSubTopic);
          setLogList(logList);
        } else {
          setLogList(data);
        }

        setMainData(data);
      })
      .catch((err) => {
        console.error('Log fetch error', err);
        setLogApiError(true);
      })
      .finally(() => {
        setLogLoading(false);
      });
  };

  const updateSubTopicValue = (sentiment: T, topic: string) => {
    setSubTopicValue((prev: { topic: string; sentiment: T }[]) => {
      const exists = prev.some((item) => item.topic === topic);
      if (exists) {
        return prev.map((item) =>
          item.topic === topic ? { topic, sentiment } : item,
        );
      }
      return prev;
    });
  };

  useEffect(() => {
    if (autoLoad) {
      getLogList();
    }
  }, []);

  return {
    logLoading,
    loading,
    logList,
    submit,
    getLogList,
    setNewValue,
    newValue,
    modifiedValue,
    subTopicValue,
    setSubTopicValue,
    updateSubTopicValue,
    submitSubTopic,
  };
}
